import { createContext, useContext, type ReactNode } from "react";

import type {
  ContactGender,
  DealStage,
  DealTypeOption,
  NoteCategoryOption,
  NoteStatus,
} from "../types";
import {
  defaultContactGender,
  defaultDarkModeLogo,
  defaultDealCategoryColors,
  defaultDealCategoryOptions,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultDealTypes,
  defaultGateStages,
  defaultLightModeLogo,
  defaultNoteCategories,
  defaultNoteStatuses,
  defaultOutreachStages,
  defaultAccountStages,
  defaultPilotStages,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";

// Define types for the context value
export interface ConfigurationContextValue {
  dealPipelineStatuses: string[];
  dealStages: DealStage[];
  dealTypes: DealTypeOption[];
  outreachStages: DealStage[];
  accountStages: DealStage[];
  pilotStages: string[];
  dealCategoryColors: Record<string, string>;
  dealCategoryOptions: { value: string; label: string }[];
  gateStages: string[];
  noteStatuses: NoteStatus[];
  noteCategories: NoteCategoryOption[];
  taskTypes: string[];
  title: string;
  darkModeLogo: string;
  lightModeLogo: string;
  contactGender: ContactGender[];
  googleWorkplaceDomain?: string;
  disableEmailPasswordAuthentication?: boolean;
  enableExport?: boolean;
}

export interface ConfigurationProviderProps
  extends Partial<ConfigurationContextValue> {
  children: ReactNode;
}

// Create context with default value
// eslint-disable-next-line react-refresh/only-export-components
export const ConfigurationContext = createContext<ConfigurationContextValue>({
  dealPipelineStatuses: defaultDealPipelineStatuses,
  dealStages: defaultDealStages,
  dealTypes: defaultDealTypes,
  outreachStages: defaultOutreachStages,
  accountStages: defaultAccountStages,
  pilotStages: defaultPilotStages,
  dealCategoryColors: defaultDealCategoryColors,
  dealCategoryOptions: defaultDealCategoryOptions,
  gateStages: defaultGateStages,
  noteStatuses: defaultNoteStatuses,
  noteCategories: defaultNoteCategories,
  taskTypes: defaultTaskTypes,
  title: defaultTitle,
  darkModeLogo: defaultDarkModeLogo,
  lightModeLogo: defaultLightModeLogo,
  contactGender: defaultContactGender,
  disableEmailPasswordAuthentication: false,
  enableExport: false,
});

export const ConfigurationProvider = ({
  children,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  dealTypes = defaultDealTypes,
  outreachStages = defaultOutreachStages,
  accountStages = defaultAccountStages,
  pilotStages = defaultPilotStages,
  dealCategoryColors = defaultDealCategoryColors,
  dealCategoryOptions = defaultDealCategoryOptions,
  gateStages = defaultGateStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  noteCategories = defaultNoteCategories,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  contactGender = defaultContactGender,
  googleWorkplaceDomain,
  disableEmailPasswordAuthentication,
  enableExport = false,
}: ConfigurationProviderProps) => (
  <ConfigurationContext.Provider
    value={{
      dealPipelineStatuses,
      dealStages,
      dealTypes,
      outreachStages,
      accountStages,
      pilotStages,
      dealCategoryColors,
      dealCategoryOptions,
      gateStages,
      darkModeLogo,
      lightModeLogo,
      noteStatuses,
      noteCategories,
      title,
      taskTypes,
      contactGender,
      googleWorkplaceDomain,
      disableEmailPasswordAuthentication,
      enableExport,
    }}
  >
    {children}
  </ConfigurationContext.Provider>
);

// eslint-disable-next-line react-refresh/only-export-components
export const useConfigurationContext = () => useContext(ConfigurationContext);
